import React, { lazy, useCallback } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import BaseView from 'containers/Base/BaseView';
import type {
  BaseViewCustomList,
  BaseViewList,
  BaseViewParams,
} from 'containers/Base/useBaseViewData';
import type { Configuration } from 'containers/Schema/schema.types';
import { getAuthtoken, useAuthtoken } from 'stores/useAuthStore';
import {
  clockingRecsQueryOptions,
  DEFAULT_CLOCKING_RECS_PARAMS,
  type ClockingRecsParams,
} from './useClockingRecsQuery';

const PayrollExportWizard = lazy(
  () => import('./dialogs/PayrollExportWizard')
);

export const clockingRecsConfiguration: Configuration = {
  itemType: 'clockingRecs',
  itemLabel: 'Clocking Record',
  listViewColumns: [
    { field: 'empID', minWidth: 150 },
    {
      field: 'firstName',
      minWidth: 200,
      excludeFromGlobalSearch: true,
      sortable: false,
    },
    {
      field: 'lastName',
      minWidth: 200,
      excludeFromGlobalSearch: true,
      sortable: false,
    },
    { field: 'startDate', minWidth: 200, excludeFromGlobalSearch: true },
    { field: 'endDate', minWidth: 200, excludeFromGlobalSearch: true },
    { field: 'active', minWidth: 100, excludeFromGlobalSearch: true },
  ],
  defaultFilterModel: {
    items: [
      {
        field: 'active',
        operator: 'equals',
        value: 'true',
      },
    ],
  },
  customDialogs: {
    payrollExport: PayrollExportWizard,
  },
  exportDisabled: true,
};

export const DEFAULT_CLOCKING_RECS_ROUTE_PARAMS: ClockingRecsParams = {
  ...DEFAULT_CLOCKING_RECS_PARAMS,
  filterModel: clockingRecsConfiguration.defaultFilterModel,
};

export const toClockingRecsParams = (
  params: BaseViewParams
): ClockingRecsParams => ({
  itemType: clockingRecsConfiguration.itemType,
  disablePagination: params.disablePagination,
  forceRefresh: params.forceRefresh,
  globalSearch: params.globalSearch,
  paginationModel:
    params.paginationModel ??
    DEFAULT_CLOCKING_RECS_ROUTE_PARAMS.paginationModel,
  filterModel:
    params.filterModel ?? DEFAULT_CLOCKING_RECS_ROUTE_PARAMS.filterModel,
  sortModel: params.sortModel ?? DEFAULT_CLOCKING_RECS_ROUTE_PARAMS.sortModel,
});

export async function fetchClockingRecsForBaseView(
  queryClient: Pick<QueryClient, 'query'>,
  authToken: string,
  params: BaseViewParams
): Promise<BaseViewList> {
  const result = await queryClient.query(
    clockingRecsQueryOptions(toClockingRecsParams(params), authToken)
  );
  return { values: result.rows, rowCount: result.rowCount };
}

export function preloadClockingRecs(
  queryClient: Pick<QueryClient, 'query'>
) {
  return queryClient
    .query(
      clockingRecsQueryOptions(
        DEFAULT_CLOCKING_RECS_ROUTE_PARAMS,
        getAuthtoken()
      )
    )
    .then(() => undefined)
    .catch(() => undefined);
}

export default function ClockingRecs() {
  const authToken = useAuthtoken();
  const queryClient = useQueryClient();
  const customList = useCallback<BaseViewCustomList>(
    (_configuration, params) =>
      fetchClockingRecsForBaseView(queryClient, authToken, params),
    [authToken, queryClient]
  );

  return (
    <BaseView
      configuration={clockingRecsConfiguration}
      customList={customList}
    />
  );
}
